import { createAsyncThunk, createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import axios from 'axios';
import { clone, cloneDeep, has, isEmpty } from 'lodash';
import moment from 'moment';
import { getThunkRejectMessage } from 'app/utils/apiError';
import { resetBoxNumbersForUncheckedOrderItems } from '../shipments/utility';
import { formatDateString, getSelectedRecords, getValidSelectedRecords, isValSelected } from '../../common/AuraFunctions';


/**
 * PGSS-5463 — a group is one customer going to one place, so its identity is both.
 *
 * The supplier order type flow identified each group by its destination alone. Two customers
 * shipping to the same address therefore shared one identity: the entity adapter is keyed by `id`,
 * so the second group overwrote the first, and a tick on what was left applied to orders the
 * operator never chose.
 *
 * It was invisible until PGSS-5457. Until then staff were not sent the destination at all, so every
 * group was keyed `undefined` and the collision was total rather than occasional.
 *
 * The outbound screen already keys `${company_name}-${shipTo}` (PGSS-5494). This is that same
 * identity, in one place, so the two cannot drift apart again. The index fallback only matters when
 * a group carries neither field, and keeps such groups distinct instead of collapsing them.
 */
export const groupIdentityOf = (item, index = 0) =>
  [item?.company_name, item?.shipTo].filter(Boolean).join('-') || `group-${index}`;

export const getAvailableOrders = createAsyncThunk(
  'backofficeApp/shipmentOrder/getAvailableOrders',
  async ({ params = {}, isNewShipment = null, isReceiveShipment = null, signal = null}, { dispatch, getState }) => {
    dispatch(setLoading());
    const path = isReceiveShipment ? `/api/availableOrdersForShipment?shipment_id=${params}` : `/api/availableOrdersForShipment?${params}`
    // Only include signal in config if it's provided to maintain backward compatibility
    const config = signal ? { signal } : {};
    const response = typeof (params) === 'string' ? await axios.get(path, config)
      : await axios.get(`/api/availableOrdersForShipment`, { params, ...config });
    let data = await response.data.availableOrders;
    if(!isNewShipment && !isReceiveShipment) {
      // PGSS-5463: was `item.id = item?.shipTo` -- the destination alone, so two customers going to
      // the same place shared one group.
      data = data?.map((item, index) => {
        item.id = groupIdentityOf(item, index)
        return item
      })
    }if(isReceiveShipment) {
      data = data?.map((item) => {
        item.id = item?.customer_name
        return item
      })
    }
    if(isNewShipment && params?.context === 'outbound_shipment') {
      // Check once if any item has a shipTo
      const hasShipTo = data?.some(item => has(item, 'shipTo'));
      if (hasShipTo) {
        // When creating a new outbound shipment,
        // assign a unique id to each item by combining company_name and shipTo.
        // This ensures proper identification and selection/unselection of orders.
        data = data?.map((item, index) => {
          item.id = groupIdentityOf(item, index)
          return item
        })
      }
    }
    // PGSS-5504: loading is cleared by the fulfilled reducer, in the same commit that puts the
    // orders in. Cleared here, it fell in the gap between emptying the list and refilling it, and
    // the screen rendered once against an empty one.
    return data || [];
  }
);

export const createShipment = createAsyncThunk(
  'backofficeApp/shipmentOrder/createShipment',
  async (inputData, { dispatch, getState, rejectWithValue }) => {
    // PGSS-5490: the side panel creates a shipment without opening the dialog, so it hands the
    // orders in directly. Falling back to the dialog's own payload keeps that path unchanged --
    // one submit for both, rather than a second one that can drift.
    const { payload: dialogPayload } = getState().backofficeApp.shipmentOrder.shipmentDialog;
    const payload = inputData.payload || dialogPayload;
    const { customer, weightRange } = getState().backofficeApp.shipmentOrder;
    const formatedDate = moment(inputData.schedule).format('YYYY-MM-DD')
    const enquiries = payload.map(p => ({ ...p, schedule: formatedDate, notes: inputData.note, shipment_type: inputData.shipment_type, pallet_count: inputData.pallet_count, is_priority: !!inputData.is_priority }))
    let data;
    try {
      const response = await axios.post('/api/createShipment', {
        enquiries,
      });
      data = await response.data;
    } catch (error) {
      // PGSS-5165: the backend refuses an order that is already on an active shipment and names
      // it in the body. Carry that message out as the reject value so the dialog can show it
      // rather than the generic fallback — the reject value survives RTK's error serialization,
      // which drops the axios response the message would otherwise have to be read from.
      return rejectWithValue(getThunkRejectMessage(error));
    }
    // Added context parameter to indicate this request is for outbound shipments.
    const params = { cid: customer ? customer.id : null, weight: weightRange || 'all', context: 'outbound_shipment'};
    dispatch(getAvailableOrders({ params, isNewShipment: true }))
    return data;
  }
);

export const viewShipment = createAsyncThunk(
  'backofficeApp/shipmentOrder/viewShipment',
  async (inputData, { dispatch }) => {
    dispatch(setLoading());
    // Added condition to avoid backend error
    if (inputData === null || inputData === undefined) {
      dispatch(resetLoading());
      const shipmentArray = new Array(1);
      return {shipment: shipmentArray};
    }
    const response = await axios.get(`api/shipment/${inputData}`)
      .finally(() => dispatch(resetLoading()))
      return response.data;
  }
);

export const editShipment = createAsyncThunk(
  'backofficeApp/shipmentOrder/editShipment',
  async (inputData, { dispatch }) => {
    const response = await axios.put(`api/shipment/${inputData?.shipment_id}?e=true`, {
      container_number: inputData?.container_number,
      eta_date: inputData?.eta_date ? formatDateString(inputData.eta_date, "yyyy-MM-dd") : "", // added eta date in the payload
    })
    return response.data;
  }
);

export const updateShipment = createAsyncThunk(
  'backofficeApp/shipmentOrder/updateShipment',
  async (params, { dispatch }) => {
    const response = await axios.put(`api/shipment/${params?.id}`, params)
    return response.data;
  }
);

// this api handles the actions for lock unlock shipment and send for approval shipment
export const updateShipmentContainer = createAsyncThunk(
  'backofficeApp/shipmentOrder/updateShipmentContainer',
  async (params, { dispatch }) => {
    const response = await axios.put(`api/updateContainer/${params?.id}`, params)
    return response.data;
  }
);

export const getOrderTypeLocations = createAsyncThunk(
  'backofficeApp/shipmentOrder/getOrderTypeLocations',
  async (params = {}, { dispatch }) => {
    const response = await axios.get(`api/getCustomerLocation`, { params });
    return response.data || [];
  }
);

export const receiveShipment = createAsyncThunk(
  'backofficeApp/shipmentOrder/receiveShipment',
  async ({shipmentId, payload}, { dispatch }) => {
    const response = await axios.put(`api/receiveShipment/${shipmentId}`, payload)
    return response.data;
  }
);

export const getReceiveStatuses = createAsyncThunk(
  'backofficeApp/shipmentOrder/getReceiveStatuses',
  async () => {
    const response = await axios.get('/api/categoryValues', { params: { c: 'receive_status' } });
    return response.data;
  }
);

export const getShipmentTypes = createAsyncThunk(
  'backofficeApp/shipmentOrder/getShipmentTypes',
  async () => {
    const response = await axios.get('/api/categoryValues', { params: { c: 'shipment_type' } });
    return response.data;
  }
);

// Outbound freight carriers (Dayton Freight, R&L Carriers, ...) offered when a shipment is
// dispatched, so the tracking number can be turned into a link the customer can follow.
export const getShippingCarriers = createAsyncThunk(
  'backofficeApp/shipmentOrder/getShippingCarriers',
  async () => {
    const response = await axios.get('/api/categoryValues', { params: { c: 'shipping_carrier' } });
    return response.data;
  }
);

export const getShipmentWeightRange = createAsyncThunk(
  'backofficeApp/shipmentOrder/getShipmentWeightRange',
  async (params = {}, { dispatch }) => {
    const response = await axios.get(`api/getWeightRange`);
    return response.data || [];
  }
);

export const getCustomers = createAsyncThunk(
  'backofficeApp/shipmentOrder/getCustomers',
  async (params = {}, { dispatch }) => {
    const response = await axios.get(`api/getCustomersForShipmentWeight`, {params});
    return response.data || [];
  }
);

// new api for create container in supplier container management
export const createContainerShipment = createAsyncThunk(
  "backofficeApp/shipmentOrder/createContainerShipment",
  async (payload, { dispatch, getState }) => {
    const response = await axios.post("api/createContainer", payload);
    const data = await response.data;
    return data;
  }
);

// shipment info view 
export const getContainerInformation = createAsyncThunk(
  'backofficeApp/shipmentOrder/getContainerInformation',
  async ({shipmentId}, { dispatch }) => {
    const response = await axios.get(`api/shipmentInfo/${shipmentId}`)
    return response.data;
  }
);

// new api for tracking container info in staff
export const getContainerTracking = createAsyncThunk(
  "backofficeApp/shipmentOrder/getContainerTracking",
  async (params, { dispatch, getState }) => {
    const response = await axios.get("api/shipmentTracking", { params });
    const data = await response.data;
    return data;
  }
);

// api to get last box number
export const getLastBoxNumber = createAsyncThunk(
  'backofficeApp/shipmentOrder/getLastBoxNumber',
  async ({shipmentId}, { dispatch }) => {
    const response = await axios.get(`api/getBoxNumber/${shipmentId}`);
    return response.data;
  }
);

// This api is used to get the list of open containers
export const getOpenContainers = createAsyncThunk(
  'backofficeApp/shipmentOrder/getOpenContainers',
  async (params, { dispatch }) => {
    const response = await axios.get(`api/getContainers` , { params });
    return response.data || [];
  }
);

export const initState = {
  loading: false,
  searchText: '',
  customer: null,
  entities: [],
  selectedItems: [],
  shipmentDialog: {
    props: {
      open: false,
    },
    data: null,
  },
  containerDialog: {
    props: {
      open: false,
    },
    data: null,
  },
  viewShipmentData: {
    shipment: []
  },
  orderTypeLocations: [],
  supplierOrderType: null,
  orderType: null,
  orderTypeLocation: null,
  shipmentReceiveStatuses: [],
  shipmentTypeNames: [],
  shippingCarrierNames: [],
  customers: [],
  shipmentWeightRanges: [],
  weightRange: 'all',
  // PGSS-4051: the ship-to filter sits in the top bar beside Customer, so it lives here with the
  // other filters rather than in one screen's local state.
  shipToFilter: null,
  containerInformation: null,
};

/**
 * PGSS-5495 — is this the group that was just ticked?
 *
 * A shipment is built for one customer and one destination, so exactly one group holds the
 * selection: the one clicked. Every other group is cleared, whoever it belongs to and wherever it
 * is going.
 *
 * Identity is the group's own key and nothing else. Two rules have been tried here and both read
 * something out of the key that is not an identity: the original asked whether one key CONTAINED
 * the other, which spared a group whose address happened to sit inside another's ("ch tn" inside
 * "ch tn 38383"), so the same customer behaved differently depending on which address was ticked
 * first; PGSS-5494 then compared `company_name`, which spared the same customer's other
 * destination on purpose. That was the wrong rule -- confirmed with the team that one destination
 * at a time is intended -- and this is deliberately not a revert to the first one.
 */
export const isTheClickedGroup = (key, clickedKey) => String(key) === String(clickedKey);

const shipmentOrdersAdapter = createEntityAdapter({});

export const { selectAll: selectOrders, selectById: selectOrderById } =
  shipmentOrdersAdapter.getSelectors((state) => state.backofficeApp.shipmentOrder);

const shipmentOrderSlice = createSlice({
  name: "backofficeApp/shipmentOrder",
  initialState: shipmentOrdersAdapter.getInitialState(initState),
  reducers: {
    setLoading: (state, action) => {
      state.loading = true;
    },
    resetLoading: (state, action) => {
      state.loading = false;
    },
    setOrdersSearchText: {
      reducer: (state, action) => {
        state.searchText = action.payload;
      },
      prepare: (event) => ({ payload: event.target.value || "" }),
    },
    setShipToFilter: (state, action) => {
      state.shipToFilter = action.payload || null;
    },
    setCustomer: (state, action) => {
      // PGSS-4051: a different customer has different addresses, so the old choice is meaningless.
      // resolveActiveShipTo would drop it anyway; clearing here keeps the control visibly empty
      // rather than briefly showing an address that no longer applies.
      state.shipToFilter = null;
      state.customer = action.payload || null;
    },
    setWeightRange: (state, action) => {
      state.weightRange = action.payload || null;
    },
    setSupplierOrderType: (state, action) => {
      state.supplierOrderType = action.payload || null;
    },
    openShipmentDialog: (state, action) => {
      state.shipmentDialog = {
        props: {
          open: true,
        },
        data: action.payload.data,
        param: action.payload.param,
        payload: action.payload.payload,
      };
    },
    closeShipmentDialog: (state, action) => {
      state.shipmentDialog = {
        props: {
          open: false,
        },
        data: null,
        param: null,
      };
    },
    openContainerDialog: (state, action) => {
      state.containerDialog = {
        props: {
          open: true,
        },
        data: action.payload.data,
        param: action.payload.param,
        payload: action.payload.payload,
      };
    },
    closeContainerDialog: (state, action) => {
      state.containerDialog = {
        props: {
          open: false,
        },
        data: null,
        param: null,
      };
    },
    updateOrderItems: (state, action) => {
      const {
        field,
        value,
        customerId,
        orderId,
        orderDetailId,
        updateCustomer,
        skipOrder,
        isSelect,
        orderItemCheckBox,
      } = action.payload;
      let changes = {};
      if (updateCustomer) {
        changes = { [field]: value };
      }
      changes = {
        ...changes,
        orders: state.entities[customerId]?.orders.map((item) => {
          let orderLevel = true;
          if (orderId != null && item.id !== orderId) {
            orderLevel = false;
          }
          if (orderLevel) {
            let updateItem = item;
            updateItem.order_detail = updateItem.order_detail.map((detail) => {
              let changeRequired = true;
              if (orderDetailId != null && detail.id !== orderDetailId) {
                changeRequired = false;
              }
              if (changeRequired) {
                if (isSelect) {
                  return {
                    ...detail,
                    [field]: value,
                    selected_quantity: detail.quantity,
                  };
                }
                return { ...detail, [field]: value, is_selected: 1 };
              }
              return detail;
            });
            if (!skipOrder) {
              const hasAnyOneOrderSelected = updateItem.order_detail.some(
                (obj) => obj.is_selected
              );
              updateItem = { ...item, [field]: hasAnyOneOrderSelected ? 1 : 0 };
            }
            return updateItem;
          }
          return item;
        }),
        split_order: state.entities[customerId]?.split_order.map((item) => {
          let selected = value;
          let requiredUpdate = true;
          if (isSelect) {
            selected = item.quantity + item.selected_quantity;
            if (
              orderDetailId &&
              (item.order_id !== orderId || item.id !== orderDetailId) &&
              !updateCustomer
            ) {
              requiredUpdate = false;
            }
            if (item.order_id !== orderId && !updateCustomer) {
              requiredUpdate = false;
            }
          } else if (item.order_id !== orderId || item.id !== orderDetailId) {
            requiredUpdate = false;
          }
          if (requiredUpdate) {
            return {
              ...item,
              selected_quantity: selected,
              quantity: item.requested_quantity - selected,
              amount: (
                (item.requested_quantity - selected) *
                item.unit_price
              ).toFixed(2),
            };
          }
          return item;
        }),
      };
      shipmentOrdersAdapter.updateOne(state, { id: customerId, changes });

      // PGSS-5495: a shipment is built for ONE customer and ONE destination, so ticking an order
      // clears every other group -- another customer, or the same customer's other address. What
      // is on screen is then always a single shipment going to a single place.
      //
      // See isTheClickedGroup for the two earlier rules and why neither is the one to go back to.
      const updatedEntities = cloneDeep(state.entities);
      const otherLocations = Object.keys(updatedEntities)
        .filter((key) => !isTheClickedGroup(key, customerId));
      otherLocations.map((customerLocation) => {
        updatedEntities[customerLocation].is_selected = 0;
        updatedEntities[customerLocation]?.orders.map((item) => {
          const updateItem = item;
            updateItem.is_selected = 0;
            updateItem.order_detail = updateItem.order_detail.map((detail) => {
              return { ...detail, is_selected: 0 };
            });
          return updateItem;
        });
        return customerLocation;
      });
      state.entities = updatedEntities;

      if (
        orderItemCheckBox === "suplier-order-item-check-box" &&
        isSelect &&
        field === "is_selected" &&
        value === 0
      ) {
        const selectedOrderInitialBoxNumber =
          resetBoxNumbersForUncheckedOrderItems(changes?.orders);
        state.entities[customerId || null].orders =
          selectedOrderInitialBoxNumber;
      }
    },
    updateContainerOrderItems: (state, action) => {
      const {
        field,
        value,
        customerId,
        orderId,
        sendBoxNumber,
        startOfNewNBoxNumber,
        // PGSS-5416: staff editing an existing container change one order at a time, and only that
        // order moves. Previously every other selected order was re-sequenced from a running
        // counter, so touching one rewrote box numbers nobody had asked to change -- the client's
        // report, and hours of manual correction. Leaving them alone can leave the sequence untidy;
        // the screen now says so rather than silently renumbering. See containerBoxSequence.js.
        preserveOtherOrderBoxNumbers = false
      } = action.payload;
      let start = parseInt(1, 10);
      // this portion is used to add box number start if already container has box numbers
      if(sendBoxNumber){
        start = startOfNewNBoxNumber;
      }
      const updatedEntities = cloneDeep(state.entities);
      const selectedOrders = [];
      updatedEntities[customerId]?.orders.map((item) => {
        if (item.id === orderId || isValSelected(item.is_selected)) {
          const selectedOrder = clone(item);
          const selectedOrderDetails = [];
          const updateItem = item;
          if (item.id === orderId) {
            updateItem[field] = value;
          }
          updateItem.order_detail = updateItem.order_detail.map((detail) => {
            if(!detail.original_qty) {
              detail.original_qty = detail.quantity;
            }
            const updateObject = {};
            if (item.id === orderId) {
              // Selected orders.
              // update is selected field
              // Remove box number if is_selected = 0
              // add box number if is_selected = 1
              updateObject[field] = value;
              if (field === "is_selected" && isValSelected(value) && detail.original_qty > 0) {
                updateObject.box_number_from = start;
                const quantity = detail?.box_quantity || detail?.quantity;
                const boxTo = start - 1 + parseInt(quantity, 10);
                updateObject.box_quantity = quantity;
                updateObject.box_number_to = boxTo;
                start = boxTo + 1;
                const selectedOrderDetail = {
                  ...updateObject,
                  actual_Quantity: detail.quantity,
                };
                selectedOrderDetails.push({
                  ...detail,
                  ...selectedOrderDetail,
                });
                return { ...detail, ...updateObject };
              }
              updateObject.box_number_from = null;
              updateObject.box_quantity = null;
              updateObject.box_number_to = null;
              return { ...detail, ...updateObject };
            }
            if (isValSelected(detail.is_selected) && detail.original_qty > 0) {
              // Other orders. Update box number only.
              // PGSS-5416: not when staff are editing an existing container -- keep what is there.
              // The order still has to be collected below so the payload is unchanged; only its box
              // numbers are left as they were.
              if (preserveOtherOrderBoxNumbers) {
                selectedOrderDetails.push({ ...detail, actual_Quantity: detail.quantity });
                return detail;
              }
              updateObject.box_number_from = start;
              const quantity = detail?.box_quantity || detail?.quantity;
              const boxTo = start - 1 + parseInt(quantity, 10);
              updateObject.box_quantity = quantity;
              updateObject.box_number_to = boxTo;
              start = boxTo + 1;
              const selectedOrderDetail = {
                ...updateObject,
                actual_Quantity: detail.quantity,
              };
              selectedOrderDetails.push({ ...detail, ...selectedOrderDetail });
              return { ...detail, ...updateObject };
            }
            return detail;
          });
          if (isValSelected(item.is_selected)) {
            selectedOrder.order_detail = selectedOrderDetails;
            selectedOrders.push(selectedOrder);
          }
          return updateItem;
        }
        return item;
      });
      // Uncheck all other location orders if present
      const otherLocations = Object.keys(updatedEntities).filter((val) => !val.includes(customerId));
      otherLocations.forEach((customerLocation) => {
        updatedEntities[customerLocation]?.orders.map((item) => {
          const updateItem = item;
          if(isValSelected(updateItem.is_selected)) {
            updateItem.is_selected = 0;
          }
          updateItem.order_detail = updateItem.order_detail.map((detail) => {
            const updateObject = detail;
            if(isValSelected(detail.is_selected)) {
              updateObject.is_selected = 0;
              updateObject.box_number_from = null;
              updateObject.box_quantity = null;
              updateObject.box_number_to = null;
            }
            return { ...detail, ...updateObject };
          })
          return updateItem;
        });
      });
      state.entities = updatedEntities;
      state.selectedItems = selectedOrders;
    },
    updateContainerOrderItem: (state, action) => {
      const {
        customerId,
        orderId,
        orderItem,
        // PGSS-5416: staff edit one order at a time from the container screen, and only that
        // order may move. Every later order keeps the box numbers it already has, even when
        // that leaves a clash or a gap -- ContainerOrdersWithDetails flags those instead.
        preserveOtherOrderBoxNumbers = false,
      } = action.payload;
      // Assign order to the entity
      // Set last boxnumber to start
      // Use start from next order
      // Update from next order
      let start = parseInt(1, 10);
      const updatedEntities = cloneDeep(state.entities);
      const selectedOrders = [];
      let updateOrder = false;
      updatedEntities[customerId]?.orders.map((item, itemIndex) => {
        const isItemSelected = isValSelected(item.is_selected);
        const selectedOrder = clone(item);
        if (item.id === orderId || updateOrder) {
          // Current order which is updated and next orders comes here
          // Change from current order
          updateOrder = true;
          if (item.id === orderId || isValSelected(item.is_selected)) {
            if (item.id === orderId) {
              const updateItem = item;
              // Selected Order. Attach the whole order return item
              const filteredRecords =  getSelectedRecords(orderItem?.order_detail);
              // Get selected the records which has qty > 0 to assgin valid start number
              const validFilteredRecords =  getValidSelectedRecords(orderItem?.order_detail);
              if (filteredRecords && filteredRecords.length > 0 && validFilteredRecords && validFilteredRecords.length > 0 ) {
                start = validFilteredRecords[validFilteredRecords.length - 1].box_number_to + 1;
                // Add to selectedOrders only if there as any order_detail is slected
                selectedOrder.order_detail = filteredRecords; // TODO: No actual_Quantity added here
                selectedOrders.push(selectedOrder);
              }
              if (!filteredRecords || isEmpty(filteredRecords)|| ( filteredRecords && filteredRecords.length <= 0)) {
                // No order details is slected so make the order is_selected to false
                updateItem.is_selected = false;
              }
              // Attach the whole order return item from the edit dialog
              updateItem.order_detail = orderItem.order_detail;
              return updateItem;
            }
            const updateItem = item;
             if(isValSelected(item.is_selected)){
              const selectedOrderDetails = [];
              updateItem.order_detail = updateItem.order_detail.map((detail) => {
                if (isValSelected(detail.is_selected) && detail.original_qty > 0) {
                  // PGSS-5416: not when staff are editing an existing container -- keep what is
                  // there, and still report the line so the payload is unchanged.
                  if (preserveOtherOrderBoxNumbers) {
                    selectedOrderDetails.push({ ...detail, actual_Quantity: detail.quantity });
                    return detail;
                  }
                  const updateObject = {};
                  updateObject.box_number_from = start;
                  const quantity = detail?.box_quantity || detail?.quantity;
                  const boxTo = start - 1 + parseInt(quantity, 10);
                  updateObject.box_quantity = quantity;
                  updateObject.box_number_to = boxTo;
                  start = boxTo + 1;
                  const selectedOrderDetail =  {...updateObject, "actual_Quantity": detail.quantity};
                  selectedOrderDetails.push({ ...detail, ...selectedOrderDetail});
                  return { ...detail, ...updateObject };
                }
                return detail;
              });
              selectedOrder.order_detail = selectedOrderDetails;
              selectedOrders.push(selectedOrder);
            }
            return updateItem;
          }
          return item;
        }
        if (isValSelected(item.is_selected)  || !updateOrder) {
          // Previous orders Comes here
          // Store the last box number to to start
          const filteredRecords = getSelectedRecords(item?.order_detail);
          // Get selected the records which has qty > 0 to assgin valid start number
          const validFilteredRecords = getValidSelectedRecords(item?.order_detail);
          if (filteredRecords && filteredRecords.length > 0 && validFilteredRecords && validFilteredRecords.length > 0) {
            start = validFilteredRecords[validFilteredRecords.length - 1].box_number_to + 1;
          }
          // Store only selected order details
          if(isItemSelected) {
            selectedOrder.order_detail = filteredRecords;
            selectedOrders.push(selectedOrder);
          }
        }
        // returned same item so previous Orders will not be changed
        return item;
      });
      state.entities = updatedEntities;
      state.selectedItems = selectedOrders;
    },
    updateBoxNumber: (state, action) => {
      // TODO shipments are based on customer
      // Supplier dont't have customer so null what to do on this case
      // But it's working with null value. Need to analyze and change
      const { customerId, orders } = action.payload;
      state.entities[customerId || null].orders = orders;
    },
    getOrderType: (state, action) => {
      state.orderType = action.payload;
    },
    setOrderTypeLocation: (state, action) => {
      state.orderTypeLocation = action.payload;
    },
    resetEntities: (state, action) => {
      state.entities = [];
      state.selectedItems = [];
    },
    resetShipmentCreation: (state, action) => {
      state.entities = [];
      state.selectedItems = [];
      state.customers = [];
      state.customer = null;
      state.weightRange = 'all';
      // PGSS-4051: cleared with the other filters. resolveActiveShipTo would drop a stale value
      // anyway, so this is tidiness rather than a fix -- but a reset that leaves one filter behind
      // is the kind of thing that becomes a real bug the moment the derivation is refactored away.
      state.shipToFilter = null;
    },
  },
  extraReducers: {
    [getAvailableOrders.fulfilled]: (state, action) => {
      shipmentOrdersAdapter.setAll(state, action);
      state.loading = false;
    },
    /**
     * PGSS-5504: clear the list through the adapter.
     *
     * This assigned `state.entities = []` -- an array, where the adapter keeps an object keyed by
     * id, and it left `state.ids` holding the previous ids. `selectAll` walks `ids` and looks each
     * one up, so every lookup missed and the screen was handed an array of `undefined` as long as
     * the list that had just been cleared.
     *
     * That is the phantom the `['undefined', undefined, null, 'null'].includes(orders[0])` guards
     * dotted around this screen were written against. The side panel added in PGSS-5490 has no such
     * guard -- it reads `group.orders` -- so after creating a shipment, which refetches, it was
     * handed `undefined` and took the whole page down with "This page could not be displayed".
     */
    [getAvailableOrders.pending]: (state) => {
      shipmentOrdersAdapter.removeAll(state);
    },
    [getAvailableOrders.rejected]: (state, action) => {
      // Prevent loading state reset when request is cancelled due to AbortController
      // This keeps the loader visible during fast filter changes until the final request completes
      // CanceledError is thrown by axios when AbortController.abort() is called
      if (action.error?.name !== 'CanceledError') {
        shipmentOrdersAdapter.removeAll(state);
        state.loading = false;
      }
    },
    [viewShipment.fulfilled]: (state, action) => {
      state.viewShipmentData = action.payload;
      // This is for edit shipment box number for supplier
      const resData = cloneDeep(action.payload.shipment);
      const selectedOrders = [];
      resData?.forEach((shipment) =>
        shipment.orders.forEach((order) =>
          {
            order.is_selected = 1;
            order.order_detail.forEach((detail) => {
              const boxNumber = detail.box_number?.split(",") || [];
              // eslint-disable-next-line
              detail.box_number_from = boxNumber[0];
              detail.box_number_to = boxNumber[boxNumber.length - 1];
              detail.is_selected = 1;
              detail.original_qty = detail.quantity;
              detail.order_no= order.order_no;
            })
            selectedOrders.push(order);
          }
        )
      );
      if (resData) {
        resData?.forEach((d, i) => {
          if (i) d.orders.forEach((order) => resData[0].orders.push(order));
        });
      }
      const shipmentVal = resData.length > 0 ? resData[0] : {}
      state.selectedItems = selectedOrders;
        shipmentOrdersAdapter.setAll(state, [shipmentVal]);
    },
    [viewShipment.rejected]: (state) => {
      state.viewShipmentData.shipment = [];
    },
    [getOrderTypeLocations.fulfilled]: (state, action) => {
      const listOfLocations = [];
      if (action.payload?.length) {
        action.payload.forEach((item) => {
          listOfLocations.push({ name: item, value: item });
        });
      }
      state.orderTypeLocations = listOfLocations || [];
    },
    [getReceiveStatuses.fulfilled]: (state, action) => {
      state.shipmentReceiveStatuses = action.payload || [];
    },
    [getShipmentTypes.fulfilled]: (state, action) => {
      state.shipmentTypeNames = action.payload.map(function (a) {
        return a.name;
      });
    },
    [getShippingCarriers.fulfilled]: (state, action) => {
      state.shippingCarrierNames = (action.payload || []).map((carrier) => carrier.name);
    },
    [getShipmentWeightRange.fulfilled]: (state, action) => {
      state.shipmentWeightRanges = action.payload || [];
    },
    [getCustomers.fulfilled]: (state, action) => {
      state.customers = action.payload || [];
    },
    [getContainerInformation.fulfilled]: (state, action) => {
      state.containerInformation = action.payload?.shipment || null;
    },
  },
});

export const {
  setLoading,
  resetLoading,
  openShipmentDialog,
  closeShipmentDialog,
  openContainerDialog,
  closeContainerDialog,
  setOrdersSearchText,
  setCustomer,
  setWeightRange,
  setShipToFilter,
  updateOrderItems,
  updateContainerOrderItems,
  updateContainerOrderItem,
  setSupplierOrderType,
  updateBoxNumber,
  getOrderType,
  setOrderTypeLocation,
  resetEntities,
  resetShipmentCreation,
} = shipmentOrderSlice.actions;

export default shipmentOrderSlice.reducer;
